import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ptBR } from '@mui/x-date-pickers/locales';
import { BranchProvider } from 'context/BranchContext';
import 'dayjs/locale/pt-br';
import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { SWRConfig } from 'swr';
import { Provider as GraphqlProvider } from 'urql';
import App from './App';
import { GlobalStyle } from './components/global-style';
import { MaintenanceContainer } from './components/maintenance';
import { theme } from './components/mui-theme';
import { fetcher } from './helper/fetcher';
import { graphqlClient as client } from './helper/graphql-client';
import { FeedbackDialogProvider } from 'context/FeedbackDialogContext';

const maskSensitiveData = (data: string): string => {
  const sensitivePatterns = [
    /"password":\s*"[^"]*"/g,
    /"creditCard":\s*{[^}]*}/g,
    /"cvv":\s*"[^"]*"/g,
    /"number":\s*"[^"]*"/g,
    /"token":\s*"[^"]*"/g,
    /"Authorization":\s*"[^"]*"/g,
    /"authorization":\s*"[^"]*"/g,
  ];

  return sensitivePatterns.reduce((masked, pattern) => {
    return masked.replace(pattern, (match) => {
      if (match.includes('creditCard')) {
        return '"creditCard":{"number":"******","cvv":"******"}';
      }
      return match.replace(/"[^"]*"$/, '"******"');
    });
  }, data);
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <PostHogProvider
        apiKey="phc_s8WE1tfoVIQhCV2OS8JgnjhyEhQu7DB14zWQApCv1Tu"
        options={{
          debug: false,
          disable_session_recording: import.meta.env.MODE === 'development',
          disable_persistence: import.meta.env.MODE === 'development',
          capture_pageview: false,
          session_recording: {
            maskCapturedNetworkRequestFn: (request: any) => {
              const maskedRequest = { ...request };

              if (maskedRequest.requestHeaders) {
                const sensitiveHeaders = ['Authorization', 'authorization', 'token'];
                sensitiveHeaders.forEach((header) => {
                  if (header in maskedRequest.requestHeaders) {
                    maskedRequest.requestHeaders[header] = '******';
                  }
                });
              }

              if (maskedRequest.requestBody) {
                maskedRequest.requestBody = maskSensitiveData(maskedRequest.requestBody);
              }

              if (maskedRequest.responseBody) {
                maskedRequest.responseBody = maskSensitiveData(maskedRequest.responseBody);
              }

              return maskedRequest;
            },
          },
        }}
      >
        <ThemeProvider theme={theme}>
          <BranchProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-br"
              localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
            >
              <GraphqlProvider value={client}>
                <SWRConfig
                  value={{
                    refreshInterval: 0,
                    fetcher,
                  }}
                >
                  <GlobalStyle />
                  <MaintenanceContainer>
                    <FeedbackDialogProvider>
                      <App />
                    </FeedbackDialogProvider>
                  </MaintenanceContainer>
                </SWRConfig>
              </GraphqlProvider>
            </LocalizationProvider>
          </BranchProvider>
        </ThemeProvider>
      </PostHogProvider>
    </BrowserRouter>
  </React.StrictMode>
);

if (import.meta.hot) {
  import.meta.hot.accept();
}
